import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import CommissionTable from "../../components/CommissionTable";

import { AuthContext } from "../../utils/userProvider";
import createAudit from "../../mutation/audit/createAuditNoHook";
import useCreateAudit from "../../mutation/audit/createAudit";
import autoGenerate from "../../utils/autoGenerate";
import {
  FormPageShell,
  FormPageCard,
  FormPageHeader,
  FormActions,
} from "../../components/ListPage";

const schema = yup.object().shape({
  agency_name: yup.string().required("Name is required"),
  agent_code: yup
    .string()
    .required("Agent Code is required")
    .max(14, "Agent Codes have a limit of 14 characters."),
  password: yup
    .string()
    .required("Agent Password is required")
    .min(4, "Agent Passwords have a minimum of 4 characters.")
    .max(8, "Agent Passwords have a limit of 8 characters."),
});

const NewAgent = (props) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [auditResponse, setAuditData, setAuditResponse] = useCreateAudit();
  const { authState, setAuthState } = useContext(AuthContext);

  const [commissionTable, setCommissionTable] = useState([]);

  const {
    control,
    register: registerAgent,
    handleSubmit: handleAgentSubmit,
    setValue,
    errors: errorsAgent,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const goBack = () => {
    window.location.href = "/dashboard/agent";
  };

  useEffect(() => {
    if (errorsAgent) {
      console.log("Errors on Create");
      console.log(errorsAgent);
    }
  }, [errorsAgent]);

  const onAgentSubmit = async (values) => {
    let dataObj = {};

    dataObj.agency_name = values.agency_name;
    dataObj.agent_code = values.agent_code;
    dataObj.password = values.password;
    dataObj.commissions = [...commissionTable];

    let agentObj = dataObj;
    delete agentObj.commissions;
    let commissionData = {
      type: "commission-structure",
      action: "create",
      agentCode: dataObj.agent_code,
    };

    console.log(dataObj);

    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          newData: agentObj,
          type: "agent",
          action: "create",
        };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        await createAudit(result.value, authState ? authState.jwt : "")
          .then(async (agent_audit) => {
            if (agent_audit.data) {
              commissionData.actionDescription = result.value.actionDescription;
              commissionData.agent_reference = agent_audit.data.id;
              for (let i = 0; i < commissionTable.length; i++) {
                commissionData.newData = commissionTable[i];
                delete commissionData.newData.__v;
                await createAudit(
                  commissionData,
                  authState ? authState.jwt : ""
                );
              }
            }
            setLoading(false);
            Toast.fire({
              icon: "success",
              title: "Agent creation Audit Created",
            });
            history.push("/dashboard/agent");
          })
          .catch((err) => {
            setLoading(false);
            setAuditResponse(err);
          });
      }
    });
  };

  const autoGenerateAgentCode = () => {
    setValue("agent_code", autoGenerate(10));
  };
  const autoGeneratePassword = () => {
    setValue("password", autoGenerate(6));
  };

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "Agent creation Audit Created",
        });
        history.push("/dashboard/agent");
      } else if (auditResponse.error) {
        let data = auditResponse.errorMessage.data;
        console.log(data);
        if (data) {
          let messageData = data.message;
          let type = messageData.type;

          if (messageData == "Agent Code Taken") {
            let itemTaken = "<ul>";

            itemTaken += "</ul>";

            Swal.fire({
              icon: "error",
              title: "Error",
              html:
                "<p>Agent Code is already taken by another Agent. Please set another Agent Code.</p>" +
                itemTaken,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: auditResponse.errorMessage.message,
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.errorMessage.message,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  return (
    <FormPageShell>
      <Box
        component="form"
        noValidate
        onSubmit={handleAgentSubmit(onAgentSubmit)}
        sx={{ width: "100%" }}
      >
        <Stack spacing={2}>
          <FormPageCard>
            <FormPageHeader
              title="New Agent"
              onBack={goBack}
              subtitle="Please fill in the required fields."
            />
            <TextField
              name="agency_name"
              margin="normal"
              fullWidth
              label="Agency Name*"
              variant="outlined"
              inputRef={registerAgent}
              error={errorsAgent.agency_name != null}
              helperText={
                errorsAgent.agency_name != null
                  ? errorsAgent.agency_name.message
                  : null
              }
              disabled={loading}
            />
            <Controller
              as={TextField}
              control={control}
              name="agent_code"
              defaultValue=""
              margin="normal"
              fullWidth
              label="Agent Code*"
              variant="outlined"
              error={errorsAgent.agent_code != null}
              helperText={
                errorsAgent.agent_code != null
                  ? errorsAgent.agent_code.message
                  : null
              }
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="auto-generate-password"
                      onClick={autoGenerateAgentCode}
                    >
                      <CachedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Controller
              as={TextField}
              control={control}
              defaultValue=""
              name="password"
              margin="normal"
              type="text"
              fullWidth
              label="Agent Code Password* (For downloading reports on the YSuites Website)*"
              variant="outlined"
              error={errorsAgent.password != null}
              helperText={
                errorsAgent.password != null
                  ? errorsAgent.password.message
                  : null
              }
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="auto-generate-password"
                      onClick={autoGeneratePassword}
                    >
                      <CachedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormPageCard>
          <FormPageCard>
            <Typography component="h1" variant="h5" fontWeight={700}>
              Agent Commission Structure
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Setup the Agent's Commission Structure here.
            </Typography>
            <CommissionTable
              data={commissionTable}
              setData={setCommissionTable}
            />
          </FormPageCard>
          <FormPageCard>
            <FormActions>
              <Button
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Submit
              </Button>
            </FormActions>
          </FormPageCard>
        </Stack>
      </Box>
    </FormPageShell>
  );
};

export default NewAgent;
